import { Locales } from '@griply/types';
import { getTimeFormat } from '@griply/utils';
import {
  useUpdatePassword,
  useUpdateUser,
  useUpdateUserSettings,
} from 'features/user';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChangePasswordDialog } from 'shared/dialogs/change-password';
import { UserSettingsDialog } from 'shared/dialogs/user-settings';
import { SettingsSection } from 'shared/dialogs/user-settings/settings-nav';
import { useDelayedCallback } from 'shared/hooks/use-delayed-callback';
import { useModifierKeysDown } from 'shared/hooks/use-modifier-keys-down';
import { useToggle } from 'shared/hooks/use-toggle';
import { useUser } from 'shared/hooks/use-user';
import {
  DateFormatOptions,
  initialDateFormat,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import {
  initialNumberFormat,
  NumberFormatOptions,
} from 'shared/types/number-format-options';
import { initialThemeMode, ThemeModes } from 'shared/types/theme-modes';
import { initialWeekStartsOn, WeekDays } from 'shared/types/week-days';
import { targetIsInput } from 'shared/utils/target-is-input';

import {
  UserSettingsContext,
  UserSettingsContextProps,
} from './user-settings-context';

export type UserSettingsProviderProps = {
  children: React.ReactNode;
};

export const UserSettingsProvider: React.FC<UserSettingsProviderProps> = ({
  children,
}) => {
  const [dialogOpen, toggleDialog, forceDialog] = useToggle(false);
  // The form re-mounts on every open (MUI unmounts a closed Dialog's children), so this is
  // read once as its initial activeSection and never has to be pushed down mid-open.
  const [settingsSection, setSettingsSection] = useState<SettingsSection>();
  const [passwordDialogOpen, togglePasswordDialog, forcePasswordDialog] =
    useToggle(false);
  const user = useUser();
  const { submit: updateUser } = useUpdateUser();
  const updateUserSettings = useUpdateUserSettings();
  const {
    updatePassword,
    isLoading: passwordLoading,
    error: passwordError,
    isSuccess: passwordSuccess,
  } = useUpdatePassword();

  const [updateUserName] = useDelayedCallback(
    useCallback(
      (name: string) => !!name && updateUser({ name }, user),
      [updateUser, user],
    ),
    1000,
  );

  const updateUserLanguage = useCallback(
    (language: Locales) => updateUser({ language }, user),
    [updateUser, user],
  );

  const updateUserDateFormat = useCallback(
    (dateFormat: DateFormatOptions) => updateUser({ dateFormat }, user),
    [updateUser, user],
  );

  const updateUserNumberFormat = useCallback(
    (numberFormat: NumberFormatOptions) => updateUserSettings({ numberFormat }),
    [updateUserSettings],
  );

  const updateUserTimeFormat = useCallback(
    (timeFormat: TimeFormatOptions) => updateUserSettings({ timeFormat }),
    [updateUserSettings],
  );

  const updateWeekStart = useCallback(
    (weekDay: WeekDays) => updateUser({ startOfWeek: weekDay }, user),
    [updateUser, user],
  );

  const updateUserThemePreference = useCallback(
    (themePreference: ThemeModes) => updateUser({ themePreference }, user),
    [updateUser, user],
  );

  // Forces open rather than toggling: a caller asking for a specific section must never
  // dismiss an already-open dialog instead of retargeting it.
  const openSettings = useCallback(
    (section?: SettingsSection) => {
      setSettingsSection(section);
      forceDialog(true);
    },
    [forceDialog],
  );

  const value = useMemo<UserSettingsContextProps>(
    () => ({
      openSettings,
      openChangePassword: togglePasswordDialog,
    }),
    [openSettings, togglePasswordDialog],
  );

  useEffect(() => {
    if (passwordSuccess) {
      forcePasswordDialog(false);
    }
  }, [forcePasswordDialog, passwordSuccess]);

  useModifierKeysDown('Comma', (e) => {
    if (targetIsInput(e.target)) {
      return; // Don't open dialog
    }
    // via openSettings, so the shortcut clears a section a previous targeted open left behind
    openSettings();
    e.preventDefault();
  });

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
      {!!user && (
        <>
          <UserSettingsDialog
            open={dialogOpen}
            onClose={toggleDialog}
            initialSection={settingsSection}
            userName={user.name}
            updateUserName={updateUserName}
            language={user.settings?.language}
            onChangeLanguage={updateUserLanguage}
            email={user.email}
            isSignedInWithProvider={user.isSiwa}
            dateFormat={user.settings?.dateFormat ?? initialDateFormat}
            onChangeDateFormat={updateUserDateFormat}
            numberFormat={user.settings?.numberFormat ?? initialNumberFormat}
            onChangeNumberFormat={updateUserNumberFormat}
            timeFormat={getTimeFormat(user)}
            onChangeTimeFormat={updateUserTimeFormat}
            weekStartsOn={user.settings?.startOfWeek ?? initialWeekStartsOn}
            onChangeWeekStart={updateWeekStart}
            onChangePassword={togglePasswordDialog}
            themePreference={
              user.settings?.themePreference?.desktop ?? initialThemeMode
            }
            onChangeThemePreference={updateUserThemePreference}
          />
          <ChangePasswordDialog
            open={passwordDialogOpen}
            onSubmit={updatePassword}
            error={passwordError ?? undefined}
            isLoading={passwordLoading}
            onClose={togglePasswordDialog}
          />
        </>
      )}
    </UserSettingsContext.Provider>
  );
};
